import { APP_ORIGIN } from 'config/constants'
import useCheckSiteStatus from 'hooks/useCheckSiteStatus'
import useSlug from 'hooks/useSlug'
import { isNonEmptyString, withTrailingSlash } from 'lib/utils'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { IMenuItem } from 'store/api'
import { selectIsXl } from 'store/ui'
import BreadcrumbMenu from './BreadcrumbMenu'
import Header from './Header'
import PromoPanel from 'components/common/PromoPanel'

// dynamic() без ssr:false → SSR HTML идентичен sync импорту (нет CLS),
// но код уходит в отдельный chunk → меньше main bundle → TBT падает.
const FooterMbl = dynamic(() => import('./FooterMbl'))
const FooterDsk = dynamic(() => import('./FooterDsk'))

const CONTENT_ONLY_KEY = 'content-only'

// Липкий content-only (webview мобильного приложения): страница, открытая
// с ?content-only=1, запоминает режим на сессию webview, чтобы шапка и
// подвал не всплывали после навигации без параметра (SPA или полной).
if (typeof window !== 'undefined') {
  try {
    if (
      new URLSearchParams(window.location.search).get(
        CONTENT_ONLY_KEY
      ) === '1'
    )
      window.sessionStorage.setItem(CONTENT_ONLY_KEY, '1')
  } catch {
    // sessionStorage недоступен — работаем только от query
  }
}

let isContentOnlyStored = () => {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(CONTENT_ONLY_KEY) === '1'
  } catch {
    return false
  }
}

type Props = {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  children: ReactNode
  hideBreadcrumbs?: boolean
  index?: boolean
  withCartPanel?: boolean
  menus?: Record<string, IMenuItem[]>
  hideFooterOnMobile?: boolean
}

let Layout = ({
  title,
  description,
  keywords,
  canonical,
  children,
  hideBreadcrumbs = false,
  index = false,
  withCartPanel = false,
  menus = {},
  hideFooterOnMobile = false,
}: Props) => {
  let { showOnlyContent, router } = useSlug()
  // Липкий флаг читаем только ПОСЛЕ маунта: чтение в рендере ломает
  // гидрацию SSG-страниц (SSR-разметка с шапкой vs клиент без шапки)
  // и роняет React в webview (белый экран на Android).
  let [contentOnlyStored, setContentOnlyStored] = useState(false)
  useEffect(() => {
    if (isContentOnlyStored()) setContentOnlyStored(true)
  }, [])
  let hideChrome = showOnlyContent || contentOnlyStored
  const { isAcceptingOrders, InfoBlock } = useCheckSiteStatus()

  // Canonical всегда указывает на «чистый» URL без query-параметров:
  // служебный content-only=1 (режим отдачи только контента, без хедера/
  // футера — см. useSlug → showOnlyContent), а также UTM и прочие метки.
  // В router.asPath они есть, но в canonical попадать не должны — иначе
  // эталонной версией страницы для поисковиков становится
  // параметризованный URL (SEO-аудит, п. 5.1).
  // Проп canonical (полный URL) перекрывает самоссылку: так страницы
  // с дублями по категории (/product/<cat>/<slug>) объявляют один адрес;
  // ему тоже дописывается слеш на конце (next.config.js, trailingSlash).
  let canonicalPath = router.asPath.split('?')[0]
  let canonicalProp =
    isNonEmptyString(canonical) && canonical.startsWith(`${APP_ORIGIN}/`)
      ? `${APP_ORIGIN}${withTrailingSlash(canonical.slice(`${APP_ORIGIN}`.length))}`
      : canonical

  const isXl = useSelector(selectIsXl)

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name='apple-mobile-web-app-title' content={title} />
        {!index && <meta name='robots' content='noindex' />}
        {isNonEmptyString(description) && (
          <meta name='description' content={description} />
        )}
        {isNonEmptyString(keywords) && (
          <meta name='keywords' content={keywords} />
        )}

        <link
          rel='canonical'
          href={
            isNonEmptyString(canonicalProp)
              ? canonicalProp
              : `${APP_ORIGIN}${canonicalPath}`
          }
        />

        {/* <link
          rel='alternate'
          href={`https://${process.env['NEXT_PUBLIC_APP_ORIGIN']}${router.asPath}`}
          hrefLang='ru'
        />

        <link
          rel='alternate'
          href={`https://${process.env['NEXT_PUBLIC_APP_ORIGIN']}/en${router.asPath}`}
          hrefLang='en'
        /> */}
      </Head>

      <div
        className={`flex flex-col justify-between w-full ${
          hideChrome ? '' : 'min-h-screen+'
        } max-w-full mx-auto`}
      >
        {!hideChrome && <Header menusSSR={menus} />}
        <div
          className={`flex-grow flex flex-col ${
            hideChrome ? '' : 'mt-[85px]'
          } lg:mt-0`}
        >
          {!isAcceptingOrders && withCartPanel && (
            <InfoBlock className='bg-alert text-accent text-xs lg:text-base2 font-semibold text-center p-3' />
          )}

          <main
            className={`${hideFooterOnMobile ? 'flex-grow lg:flex-grow-0' : 'flex-grow-0'} max-w-[100vw] relative flex`}
          >
            {withCartPanel ? (
              <>
                <div className='border-r border-lightGrey relative flex-grow w-full xl:max-w-[calc(100%-300px)]'>
                  {children}
                </div>

                {isXl && <PromoPanel />}
              </>
            ) : (
              <div className='relative flex-grow w-full'>
                {!hideBreadcrumbs && (
                  <BreadcrumbMenu menusSSR={menus} />
                )}
                {children}
              </div>
            )}
          </main>
        </div>

        {!hideChrome && !hideFooterOnMobile && (
          <div className='lg:hidden'>
            <FooterMbl />
          </div>
        )}

        {!hideChrome && (
          <div className='hidden lg:block'>
            <FooterDsk />
          </div>
        )}
      </div>
    </>
  )
}

export default Layout
